/**
 * Data Import Wizard — existing-table auto-map + validation (S6.T1, relocated
 * + evolved to raw target-column FACTS S6.T2a).
 *
 * `autoMapColumns` proposes a starting `ImportTargetMapping[]` by normalized
 * source↔target name matching. `validateMapping` turns a (possibly
 * user-edited) mapping into per-target issues: a blocking `unmapped-required`,
 * `canAssign`-driven `type-risk`, and the Rev-6/Fable-CP4-corrected
 * `nullMismatch`.
 *
 * Process-agnostic shared module (src/features/workspace/data-import/mapping/):
 * no React/zustand/DOM/electron-runtime imports — both the renderer and the
 * main process import this SAME code (S6.T2a design ruling, "one gate, no
 * second list"). `ImportColumnPlan`/`ImportTargetColumn`/`ImportTargetMapping`/
 * `CanonicalType` are type-only imports, erased at build — routed through
 * `src/types/electron.d.ts` (the codebase's sanctioned crossing point) rather
 * than a direct `electron/*.ts` import, which trips the tsconfig.node.json
 * composite project-reference boundary (TS6305) for a fully-checked `src/` file.
 */
import type { CanonicalType, ImportColumnPlan, ImportTargetColumn, ImportTargetMapping } from '../../../../types/electron';
import { canAssign, type AssignabilityVerdict } from './assignability';

export type ImportMappingIssue =
  | { kind: 'unmapped-required'; targetColumn: string }
  | { kind: 'type-risk'; targetColumn: string; severity: Extract<AssignabilityVerdict, 'lossy' | 'invalid'>; sourceCanonical: CanonicalType; targetCanonical: CanonicalType }
  | { kind: 'nullMismatch'; targetColumn: string; nullCount: number };

/** Case-insensitive, non-alphanumeric-stripped match key. Normalizes BOTH
 *  sides (Oracle catalog names arrive exact-case UPPERCASE) — never mutates
 *  the stored `name`/`sourceName`, only used for the comparison. */
function normalizeNameForMatch(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Propose a starting mapping by normalized-name matching, one entry per
 * target that finds a same-named INCLUDED source column. Never auto-maps a
 * computed/generated target, the SQLite synthetic rowid, or a non-insertable
 * identity (PG/Oracle — `identityInsertable === false`). An
 * `identityInsertable === true` target (e.g. MSSQL identity) MAY be
 * auto-mapped by name — identity is mapping-only (CP4), not auto-map-excluded.
 * An unmapped target simply gets no entry — there is no null/unmapped variant
 * on `ImportTargetMapping`.
 */
export function autoMapColumns(sources: ImportColumnPlan[], targets: ImportTargetColumn[]): ImportTargetMapping[] {
  const mappings: ImportTargetMapping[] = [];
  // A file source maps to AT MOST ONE target (the same invariant the store's
  // atomic steal enforces for manual edits). If two targets normalize to the
  // same name (e.g. `user_id` and `userid` both strip to `userid`), first
  // target wins — a claimed source is never re-used for a later target.
  const claimed = new Set<number>();
  for (const target of targets) {
    if (target.isComputedOrGenerated || target.isSyntheticRowid || target.identityInsertable === false) continue;
    const key = normalizeNameForMatch(target.name);
    const match = sources.find((s) => s.include && !claimed.has(s.index) && normalizeNameForMatch(s.name) === key);
    if (match) {
      claimed.add(match.index);
      mappings.push({ targetColumn: target.name, source: { kind: 'file', sourceIndex: match.index } });
    }
  }
  return mappings;
}

/**
 * DORMANT mapping (deferred-backlog #1): a `kind:'file'` mapping whose source
 * column exists but is currently `include: false`. The mapping is KEPT in the
 * stored `ImportTargetMapping[]` (re-including the source restores it exactly),
 * but it contributes NOTHING while dormant — coverage, validation, and the
 * main-process insert projection (`electron/data-import/insertProjection.ts`
 * mirrors this same check) all treat the target as unmapped. A dangling
 * `sourceIndex` (no matching column at all) is NOT dormant — each consumer
 * keeps its own pre-existing defensive handling for that case.
 */
export function isDormantFileMapping(
  mapping: ImportTargetMapping,
  sourceByIndex: ReadonlyMap<number, ImportColumnPlan>,
): boolean {
  if (mapping.source.kind !== 'file') return false;
  const source = sourceByIndex.get(mapping.source.sourceIndex);
  return source !== undefined && !source.include;
}

/**
 * Validate a (possibly user-edited) mapping against the source columns and
 * target table shape. Returns one issue per target that needs attention —
 * a clean target has no entry. A dormant mapping (excluded source, see
 * `isDormantFileMapping`) is treated exactly as if the target were unmapped:
 * it can re-raise `unmapped-required` and never contributes type/null issues.
 */
export function validateMapping(
  mapping: ImportTargetMapping[],
  sources: ImportColumnPlan[],
  targets: ImportTargetColumn[],
): ImportMappingIssue[] {
  const issues: ImportMappingIssue[] = [];
  const sourceByIndex = new Map(sources.map((s) => [s.index, s]));
  const mappingByTarget = new Map(mapping.map((m) => [m.targetColumn, m]));

  for (const target of targets) {
    let m = mappingByTarget.get(target.name);
    if (m && isDormantFileMapping(m, sourceByIndex)) m = undefined; // dormant ⇒ unmapped

    if (!m) {
      // `unmapped-required` fires ONLY for a NOT-NULL target with no default,
      // no mapping, no fixed-value, that is neither computed/generated, nor
      // an identity column (any engine-insertability), nor the SQLite
      // synthetic rowid — the DB fills all three of those itself.
      if (
        !target.isComputedOrGenerated &&
        target.identityInsertable === null &&
        !target.isSyntheticRowid &&
        !target.isNullable &&
        !target.hasDefault
      ) {
        issues.push({ kind: 'unmapped-required', targetColumn: target.name });
      }
      continue;
    }

    if (m.source.kind === 'fixed') continue; // S6.T4's concern — no type/null checks here

    const source = sourceByIndex.get(m.source.sourceIndex);
    if (!source) continue; // defensive: dangling mapping index

    const verdict = canAssign(source.canonical, target.canonical);
    if (verdict !== 'ok') {
      issues.push({
        kind: 'type-risk',
        targetColumn: target.name,
        severity: verdict,
        sourceCanonical: source.canonical,
        targetCanonical: target.canonical,
      });
    }

    // Rev 6 / Fable CP4 CORRECTED rule: on the INSERT we bind an explicit
    // NULL for null rows; a column default never applies to an explicitly
    // bound NULL (true on all 5 engines). So a mapped source with nulls into
    // a NOT-NULL target warns REGARDLESS of whether the target has a
    // default — the default is irrelevant on the mapped path. (The
    // unmapped-NOT-NULL-with-default "clean" case lives on the unmapped
    // branch above, where the column is omitted from the INSERT so the DB
    // default fills it — an S6.T3 runner invariant, not this function's.)
    if (source.stats.nulls > 0 && !target.isNullable) {
      issues.push({ kind: 'nullMismatch', targetColumn: target.name, nullCount: source.stats.nulls });
    }
  }

  return issues;
}

/**
 * Reverse of the target-keyed mapping (S6.T2c-2a): file source index → the
 * target column it currently feeds. The grid-header mapping surface is
 * SOURCE-first (each file column's header shows which target it feeds) while
 * `ImportTargetMapping[]` itself is target-first — this is the lookup that
 * bridges the two. A `kind:'fixed'` mapping has no source column, so it
 * contributes nothing here.
 */
export function targetBySourceIndex(mapping: ImportTargetMapping[]): Map<number, string> {
  const result = new Map<number, string>();
  for (const m of mapping) {
    if (m.source.kind === 'file') result.set(m.source.sourceIndex, m.targetColumn);
  }
  return result;
}

/**
 * For each `kind:'file'` mapping, the resolved TARGET canonical the source
 * column must satisfy — keyed by source index. Prevalidation (S3.T2-style)
 * needs this to validate file VALUES against the actual target type,
 * independent of the structural verdict `canAssign` already computed.
 */
export function resolveTargetCanonicalsBySource(
  mapping: ImportTargetMapping[],
  targets: ImportTargetColumn[],
): Map<number, CanonicalType> {
  const targetByName = new Map(targets.map((t) => [t.name, t]));
  const result = new Map<number, CanonicalType>();
  for (const m of mapping) {
    if (m.source.kind !== 'file') continue;
    const target = targetByName.get(m.targetColumn);
    if (!target) continue;
    result.set(m.source.sourceIndex, target.canonical);
  }
  return result;
}
